import { useCallback, useMemo } from "react";
import { useTheme } from "next-themes";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import { go } from "@codemirror/lang-go";
import { python } from "@codemirror/lang-python";
import { javascript } from "@codemirror/lang-javascript";
import { vim } from "@replit/codemirror-vim";
import {
  lightThemeExtensions,
  darkThemeExtensions,
} from "../shared/code-theme";

interface CodeEditorProps {
  value: string;
  language: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  minHeight?: string;
  placeholder?: string;
  vimEnabled?: boolean;
}

// Map language strings to CodeMirror language extensions
// Note: Only Go, Python, and JavaScript are currently installed
function getLanguageExtension(language: string) {
  switch (language) {
    case "go":
      return go();
    case "python":
      return python();
    case "javascript":
    case "js":
      return javascript();
    case "typescript":
    case "ts":
      return javascript({ typescript: true });
    // For languages without dedicated packages, use JavaScript as a fallback
    // This provides basic syntax highlighting via similar constructs
    case "java":
    case "kotlin":
    case "csharp":
    case "swift":
      return javascript(); // C-style syntax similar enough
    case "rust":
    case "cpp":
    case "c":
      return javascript(); // Fallback
    case "ruby":
      return python(); // Similar indentation-based style
    case "sql":
    default:
      return javascript();
  }
}

export function CodeEditor({
  value,
  language,
  onChange,
  disabled = false,
  minHeight = "400px",
  placeholder,
  vimEnabled = false,
}: CodeEditorProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const handleChange = useCallback(
    (val: string) => {
      if (!disabled) {
        onChange(val);
      }
    },
    [onChange, disabled]
  );

  const extensions = useMemo(() => {
    const langExtension = getLanguageExtension(language);
    const themeExtensions = isDark
      ? darkThemeExtensions
      : lightThemeExtensions;

    return [
      // Vim extension must be first to properly intercept key events
      ...(vimEnabled ? [vim()] : []),
      langExtension,
      ...themeExtensions,
      EditorView.lineWrapping,
      EditorView.editable.of(!disabled),
    ];
  }, [language, isDark, disabled, vimEnabled]);

  return (
    <div
      className="rounded-lg border overflow-hidden"
      style={{ minHeight }}
    >
      <CodeMirror
        value={value}
        onChange={handleChange}
        extensions={extensions}
        placeholder={placeholder}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightActiveLine: true,
          foldGutter: true,
          dropCursor: true,
          allowMultipleSelections: true,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: true,
          rectangularSelection: true,
          crosshairCursor: false,
          highlightSelectionMatches: true,
          searchKeymap: true,
          tabSize: 2,
        }}
        style={{ minHeight }}
        editable={!disabled}
      />
    </div>
  );
}
